import { AntennasGame } from '../../games/antennas/AntennasGame.js';
import { AntennasVortex } from '../../games/antennas/AntennasVortex.js';
import { AntennasRenderer } from '../../games/antennas/AntennasRenderer.js';
import { Grid } from '../../core/Grid.js';
import { Position, positionKey } from '../../core/GridGeometry.js';
import { RenderConfig } from '../../rendering/Renderer.js';
import { TutorialGameAdapter } from '../TutorialEngine.js';

// ── Antennas-specific types ──────────────────────────────────────

export interface AntennasRenderOptions {
    // Placeholder — AntennasRenderer is fully game-state driven
}

// ── Adapter ─────────────────────────────────────────────────────

export class AntennasTutorialAdapter implements TutorialGameAdapter {
    private game: AntennasGame;
    private renderer: AntennasRenderer;
    private grid: Grid;

    constructor(canvas: HTMLCanvasElement, grid: Grid) {
        this.grid = grid;
        this.game = new AntennasGame(grid);
        this.renderer = new AntennasRenderer(canvas, grid);
        // Static scenes: no tweening, so the renderer never self-re-renders and
        // wipes the engine-drawn annotations.
        this.renderer.setSelfAnimation(false);
    }

    applyState(state: unknown): void {
        const raw = state as string;
        this.game.reset();

        if (!raw) return;

        // Parse "antennaString+click1,click2" notation
        // Left of '+': standard antenna string for deserialize()
        // Right of '+': comma-separated positions to simulate via handleClick()
        const plusIndex = raw.indexOf('+');
        let antennaPart: string;
        let clickPart: string;

        if (plusIndex >= 0) {
            antennaPart = raw.substring(0, plusIndex);
            clickPart = raw.substring(plusIndex + 1);
        } else {
            antennaPart = raw;
            clickPart = '';
        }

        if (antennaPart) {
            this.game.deserialize(antennaPart);
        }

        if (clickPart) {
            for (const posStr of clickPart.split(',')) {
                const trimmed = posStr.trim();
                if (!trimmed) continue;
                const pos = this.grid.serializer.deserializePosition(trimmed);
                this.game.handleClick(pos);
            }
        }
    }

    render(_options: unknown): void {
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createAntennasTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    return new AntennasTutorialAdapter(canvas, grid);
}

// ── Vortex state ─────────────────────────────────────────────────

export interface AntennasVortexTutorialState {
    antennas: string;
    gateMode?: boolean;
    /**
     * Extra gate pairs (cell strings, e.g. ['B5', 'D5']) merged on top of the
     * adapter's default teleport pair for this scene only — used by the
     * activation scene to show a second, blocked (amber) pair. Omit otherwise.
     */
    extraGates?: Array<[string, string]>;
}

/** Parse a cell string like "B5" into a Position ({ row: 5, col: 1 }). */
function parseCellString(s: string): Position {
    return { col: s.charCodeAt(0) - 65, row: parseInt(s.slice(1), 10) };
}


// ── Vortex Adapter ───────────────────────────────────────────────

export class AntennasVortexTutorialAdapter implements TutorialGameAdapter {
    private game: AntennasVortex;
    private renderer: AntennasRenderer;
    private gatePairs: Array<[Position, Position]>;
    private allGates: Set<string>;
    private currentGateMode: boolean = false;

    constructor(
        canvas: HTMLCanvasElement,
        grid: Grid,
        gatePairs: Array<[Position, Position]>,
    ) {
        this.game = new AntennasVortex(grid);
        this.renderer = new AntennasRenderer(canvas, grid);
        this.renderer.setSelfAnimation(false);   // static scenes — see the Base adapter
        this.gatePairs = gatePairs;
        this.allGates = new Set<string>();
        for (const [a, b] of gatePairs) {
            this.allGates.add(positionKey(a));
            this.allGates.add(positionKey(b));
        }
    }

    applyState(state: unknown): void {
        const s = state as AntennasVortexTutorialState | null;
        this.game.reset();
        const pairs = [...this.gatePairs];
        for (const [a, b] of s?.extraGates ?? []) {
            pairs.push([parseCellString(a), parseCellString(b)]);
        }
        const allGates = new Set<string>();
        for (const [a, b] of pairs) {
            allGates.add(positionKey(a));
            allGates.add(positionKey(b));
        }
        this.game.setVortexData(pairs, allGates);
        if (s?.antennas) {
            this.game.deserialize(s.antennas);
        }
        this.currentGateMode = s?.gateMode ?? false;
    }

    render(_options: unknown): void {
        this.renderer.setGateMode(this.currentGateMode);
        this.renderer.render(this.game);
    }

    getConfig(): RenderConfig {
        return this.renderer.getConfig();
    }
}

export function createAntennasVortexTutorialAdapter(
    canvas: HTMLCanvasElement,
    grid: Grid,
): TutorialGameAdapter {
    // Gate pair for the vortex antennas tutorial: E0 ↔ B1
    const gatePairs: Array<[Position, Position]> = [
        [{ row: 0, col: 4 }, { row: 1, col: 1 }],
    ];
    return new AntennasVortexTutorialAdapter(canvas, grid, gatePairs);
}
